import React, { useEffect, useRef } from "react";
import { Track } from "livekit-client";
import { TrackReferenceOrPlaceholder } from "@livekit/components-core";

// Helper to convert hex to RGB
function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16),
    }
    : { r: 249, g: 115, b: 22 }; // Default orange
}

// Convert color to brighter neon version
function toNeonColor(hex: string, brightness: number = 1.5): string {
  const rgb = hexToRgb(hex);
  // Brighten the color for neon effect
  const r = Math.min(255, Math.floor(rgb.r * brightness));
  const g = Math.min(255, Math.floor(rgb.g * brightness));
  const b = Math.min(255, Math.floor(rgb.b * brightness));
  return `rgba(${r}, ${g}, ${b}, 1)`;
}

function toNeonColorWithAlpha(hex: string, alpha: number, brightness: number = 1.3): string {
  const rgb = hexToRgb(hex);
  const r = Math.min(255, Math.floor(rgb.r * brightness));
  const g = Math.min(255, Math.floor(rgb.g * brightness));
  const b = Math.min(255, Math.floor(rgb.b * brightness));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

interface CircularVisualizerProps {
  trackRef?: TrackReferenceOrPlaceholder;
  className?: string;
  color?: string;
  radius?: number;
  agentState?: string;
  primaryColor?: string;
}

export const CircularVisualizer: React.FC<CircularVisualizerProps> = ({
  trackRef,
  className,
  color = "rgba(0, 255, 255, 1)",
  radius: customRadius,
  agentState = "listening",
  primaryColor,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number | null>(null);
  const scaleRef = useRef(1);
  const targetScaleRef = useRef(1);
  const shakeOffsetRef = useRef({ x: 0, y: 0 });
  const track = trackRef?.publication?.track;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d")!;

    // --- NEON SPHERE SETTINGS ---
    const particleCount = 1000;
    const baseRadius = customRadius ?? Math.min(canvas.width, canvas.height) * 0.45;

    const particles = Array.from({ length: particleCount }).map((_, i) => {
      const phi = Math.acos(1 - (2 * (i + 0.5)) / particleCount);
      const theta = Math.PI * (1 + Math.sqrt(5)) * i;

      return {
        x: baseRadius * Math.sin(phi) * Math.cos(theta),
        y: baseRadius * Math.sin(phi) * Math.sin(theta),
        z: baseRadius * Math.cos(phi),
        size: 0.5 + Math.random() * 0.6,
        seed: Math.random() * Math.PI * 2,
      };
    });

    let audioContext: AudioContext | null = null;
    let analyser: AnalyserNode | null = null;
    let buffer: Uint8Array | null = null;

    if (track && track.kind === Track.Kind.Audio) {
      audioContext = new AudioContext();
      const source = audioContext.createMediaStreamSource(
        new MediaStream([track.mediaStreamTrack])
      );

      analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;

      source.connect(analyser);
      buffer = new Uint8Array(analyser.frequencyBinCount);
    }

    let rotation = 0;

    // Use primaryColor with fallback, then generate neon colors
    const colorToUse = primaryColor || "#f97316"; // Fallback to orange
    const neonCore = toNeonColor(colorToUse, 1.8); // Very bright core
    const neonMid = toNeonColorWithAlpha(colorToUse, 0.8, 1.4);
    const neonOuter = toNeonColorWithAlpha(colorToUse, 0.4, 1.2);
    const neonAmbient = toNeonColorWithAlpha(colorToUse, 0.08, 1.0);

    const draw = () => {
      // Handle dynamic resizing
      if (canvas.width !== canvas.clientWidth || canvas.height !== canvas.clientHeight) {
        canvas.width = canvas.clientWidth;
        canvas.height = canvas.clientHeight;
      }

      const w = canvas.width;
      const h = canvas.height;

      ctx.clearRect(0, 0, w, h);

      let volume = 0;
      if (analyser && buffer) {
        analyser.getByteFrequencyData(buffer as any);
        volume = buffer.reduce((a, b) => a + b) / buffer.length / 255;
      }

      // Animate scale based on agent state
      if (agentState === "speaking") {
        targetScaleRef.current = 1.1 + volume * 0.3;
        shakeOffsetRef.current = {
          x: (Math.random() - 0.5) * 4 * volume,
          y: (Math.random() - 0.5) * 4 * volume,
        };
      } else if (agentState === "thinking") {
        targetScaleRef.current = 0.95 + Math.sin(performance.now() * 0.003) * 0.05;
        shakeOffsetRef.current = { x: 0, y: 0 };
      } else {
        targetScaleRef.current = 0.9;
        shakeOffsetRef.current = { x: 0, y: 0 };
      }

      scaleRef.current += (targetScaleRef.current - scaleRef.current) * 0.1;
      const currentScale = scaleRef.current;

      const cx = w / 2 + shakeOffsetRef.current.x;
      const cy = h / 2 + shakeOffsetRef.current.y;

      rotation += agentState === "speaking" ? 0.003 : 0.0015;

      const sorted = [...particles].sort((a, b) => b.z - a.z);

      for (const p of sorted) {
        const breath = Math.sin(p.seed + performance.now() * 0.0003) * 2;

        const cos = Math.cos(rotation);
        const sin = Math.sin(rotation);

        const rx = (p.x * cos - p.z * sin) * currentScale;
        const rz = (p.x * sin + p.z * cos) * currentScale;
        const ry = (p.y + breath * (0.8 + volume * 2)) * currentScale;

        const perspective = 280;
        const scale = perspective / (perspective + rz);

        const px = rx * scale + cx;
        const py = ry * scale + cy;

        const baseOpacity = Math.max(0.2, Math.min(0.9, scale * 0.8));
        const opacity = baseOpacity + volume * 0.3;

        const glowSize = p.size * scale * (1 + volume * 0.5);

        // Outer glow
        ctx.beginPath();
        ctx.arc(px, py, glowSize * 3, 0, Math.PI * 2);
        ctx.globalAlpha = opacity * 0.15;
        ctx.fillStyle = neonOuter;
        ctx.fill();

        // Mid glow
        ctx.beginPath();
        ctx.arc(px, py, glowSize * 2, 0, Math.PI * 2);
        ctx.globalAlpha = opacity * 0.3;
        ctx.fillStyle = neonMid;
        ctx.fill();

        // Core particle - brightest neon
        ctx.beginPath();
        ctx.arc(px, py, glowSize, 0, Math.PI * 2);
        ctx.globalAlpha = opacity;
        ctx.fillStyle = neonCore;
        ctx.fill();

        ctx.globalAlpha = 1.0;
      }

      // Ambient glow using primary color
      const gradient = ctx.createRadialGradient(w / 2, h / 2, baseRadius * 0.3, w / 2, h / 2, baseRadius * 1.5);
      gradient.addColorStop(0, neonAmbient);
      gradient.addColorStop(0.5, toNeonColorWithAlpha(primaryColor || "#e91717ff", 0.03, 0.8));
      gradient.addColorStop(1, "rgba(0, 0, 0, 0)");

      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, w, h);

      rafRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (audioContext) audioContext.close();
    };
  }, [track, color, agentState, customRadius, primaryColor]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{ width: "100%", height: "100%" }}
    />
  );
};
